<template>
  <b-form @submit.stop.prevent="handleSubmit">
    <b-row>
      <b-col cols="12">
        <b-form-group
          v-if="newForm"
          :label="formLabels.document_type"
          label-for="compliance-document-document_type"
        >
          <category-button-select
            id="compliance-document-document_type"
            name="compliance-document-document_type"
            v-model="$v.formData.document_type.$model"
            :select-class="{ 'is-invalid': $v.formData.document_type.$dirty && $v.formData.document_type.$error }"
            :options="documentTypeOptions"
          />
          <small
            v-if="!formData.document_type"
            class="form-text text-secondary"
          >
            Choose the type of document to continue
          </small>
          <b-form-invalid-feedback
            v-if="$v.formData.document_type.$dirty"
            class="compliance-document-document_type-feedback"
          >
            <span v-if="!$v.formData.document_type.required">Please select a document type.</span>
            <span v-if="!$v.formData.document_type.serverFailed">{{ serverErrors.document_type }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <template v-if="showFormFields">
      <b-row>
        <b-col cols="12">
          <b-form-group
            v-if="newForm"
            :label="formLabels.user_id"
            label-for="compliance-document-user_id"
          >
            <user-select
              name="compliance-document-user_id"
              :select-class="{ 'is-invalid': $v.formData.user_id.$dirty && $v.formData.user_id.$error }"
              :value="formData.user"
              @input="onUserChange"
              :filter-params="userSelectParams"
            />
            <b-form-invalid-feedback
              v-if="$v.formData.user_id.$dirty"
              class="compliance-document-user_id-feedback"
            >
              <span v-if="!$v.formData.user_id.required">Please enter a user.</span>
              <span v-if="!$v.formData.user_id.serverFailed">
                {{ serverErrors.user_id }}
              </span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col
          cols="12"
          lg="6"
        >
          <b-form-group
            :label="formLabels.number"
            label-for="compliance-document-number"
          >
            <b-form-input
              name="compliance-document-number"
              v-model="$v.formData.number.$model"
              :state="$v.formData.number.$dirty ? !$v.formData.number.$error : null"
              type="text"
            />
            <b-form-invalid-feedback
              v-if="$v.formData.number.$dirty"
              class="compliance-document-number-feedback"
            >
              <span v-if="!$v.formData.number.required">Please enter a number.</span>
              <!-- prettier-ignore -->
              <span v-if="!$v.formData.number.maxLength">
                The number must be less than {{ $v.formData.number.$params.maxLength.max + 1 }} characters.
              </span>
              <span v-if="!$v.formData.number.serverFailed">{{ serverErrors.number }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          lg="6"
        >
          <b-form-group
            :label="formLabels.category"
            label-for="compliance-document-category"
          >
            <category-select
              v-if="showInsuranceCategoryInput"
              name="compliance-document-category"
              v-model="$v.formData.category.$model"
              :select-class="{ 'is-invalid': $v.formData.category.$dirty && $v.formData.category.$error }"
              :options="categoryOptions"
            />
            <b-form-input
              v-else
              name="compliance-document-category"
              v-model="$v.formData.category.$model"
              :state="$v.formData.category.$dirty ? !$v.formData.category.$error : null"
              type="text"
            />
            <b-form-invalid-feedback
              v-if="$v.formData.category.$dirty"
              class="compliance-document-category-feedback"
            >
              <span v-if="!$v.formData.category.required">Please enter a {{ terminology.category }}.</span>
              <span v-if="!$v.formData.category.maxLength">
                The {{ terminology.category }} must be less than {{ $v.formData.category.$params.maxLength.max + 1 }} characters.
              </span>
              <span v-if="!$v.formData.category.serverFailed">{{ serverErrors.category }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col
          cols="12"
          lg="6"
        >
          <b-form-group
            :label="formLabels.issuer_name"
            label-for="compliance-document-issuer_name"
          >
            <b-form-input
              name="compliance-document-issuer_name"
              v-model="$v.formData.issuer_name.$model"
              :state="$v.formData.issuer_name.$dirty ? !$v.formData.issuer_name.$error : null"
              type="text"
            />
            <b-form-invalid-feedback
              v-if="$v.formData.issuer_name.$dirty"
              class="compliance-document-issuer_name-feedback"
            >
              <span v-if="!$v.formData.issuer_name.maxLength">
                The {{ terminology.issuerName }} must be less than {{ $v.formData.issuer_name.$params.maxLength.max + 1 }} characters.
              </span>
              <span v-if="!$v.formData.issuer_name.serverFailed">{{ serverErrors.issuer_name }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          lg="6"
        >
          <b-form-group
            :label="formLabels.state"
            label-for="compliance-document-state"
          >
            <b-form-input
              name="compliance-document-state"
              v-model="$v.formData.state.$model"
              :state="$v.formData.state.$dirty ? !$v.formData.state.$error : null"
              type="text"
            />
            <b-form-invalid-feedback
              v-if="$v.formData.state.$dirty"
              class="compliance-document-state-feedback"
            >
              <span v-if="!$v.formData.state.maxLength">
                The state must be less than {{ $v.formData.state.$params.maxLength.max + 1 }} characters.
              </span>
              <span v-if="!$v.formData.state.serverFailed">{{ serverErrors.state }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col
          cols="12"
          lg="6"
        >
          <b-form-group
            v-if="newForm"
            :label="formLabels.start_date"
            label-for="compliance-document-start_date"
          >
            <sfs-date-picker
              name="compliance-document-start_date"
              v-model="$v.formData.start_date.$model"
              :class="{ 'is-invalid': $v.formData.start_date.$dirty && $v.formData.start_date.$error }"
              :with-class="{ 'is-invalid': $v.formData.start_date.$dirty && $v.formData.start_date.$error }"
            />
            <b-form-invalid-feedback
              v-if="$v.formData.start_date.$dirty"
              :state="$v.formData.start_date.$dirty ? !$v.formData.start_date.$error : null"
              class="compliance-document-start_date-feedback"
            >
              <span v-if="!$v.formData.start_date.serverFailed">
                {{ serverErrors.start_date }}
              </span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          lg="6"
        >
          <b-form-group
            v-if="newForm"
            :label="formLabels.end_date"
            label-for="compliance-document-end_date"
          >
            <sfs-date-picker
              name="compliance-document-end_date"
              v-model="$v.formData.end_date.$model"
              :class="{ 'is-invalid': $v.formData.end_date.$dirty && $v.formData.end_date.$error }"
              :with-class="{ 'is-invalid': $v.formData.end_date.$dirty && $v.formData.end_date.$error }"
            />
            <b-form-invalid-feedback
              v-if="$v.formData.end_date.$dirty"
              :state="$v.formData.end_date.$dirty ? !$v.formData.end_date.$error : null"
              class="compliance-document-end_date-feedback"
            >
              <span v-if="!$v.formData.end_date.required">{{ formLabels.end_date_required }}</span>
              <span v-if="!$v.formData.end_date.serverFailed">
                {{ serverErrors.end_date }}
              </span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col
          cols="12"
          lg="6"
        >
          <b-form-group
            :label="formLabels.email"
            label-for="compliance-document-email"
          >
            <b-form-input
              name="compliance-document-email"
              v-model="$v.formData.email.$model"
              autocomplete="new-password"
              :state="$v.formData.email.$dirty ? !$v.formData.email.$error : null"
              type="text"
              placeholder="example@email.com"
            />
            <b-form-invalid-feedback
              v-if="$v.formData.email.$dirty"
              class="compliance-document-email-feedback"
            >
              <span v-if="!$v.formData.email.email">Please enter a valid email.</span>
              <span v-if="!$v.formData.email.maxLength">
                The email must be less than {{ $v.formData.email.$params.maxLength.max + 1 }} characters.
              </span>
              <span v-if="!$v.formData.email.serverFailed">{{ serverErrors.email }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          lg="6"
        >
          <b-form-group
            :label="formLabels.phone"
            label-for="compliance-document-phone"
          >
            <b-form-input
              name="compliance-document-phone"
              v-model="$v.formData.phone.$model"
              :state="$v.formData.phone.$dirty ? !$v.formData.phone.$error : null"
              type="text"
              placeholder="(201) 555-5555"
            />
            <b-form-invalid-feedback
              v-if="$v.formData.phone.$dirty"
              class="compliance-document-phone-feedback"
            >
              <span v-if="!$v.formData.phone.maxLength">
                The phone must be less than {{ $v.formData.phone.$params.maxLength.max + 1 }} characters.
              </span>
              <span v-if="!$v.formData.phone.serverFailed">{{ serverErrors.phone }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col
          cols="12"
          lg="6"
        >
          <b-form-group label-for="compliance-document-reminder_days">
            <template #label>
              <span>{{ formLabels.reminder_days }}</span>
              <i
                id="reminder-days-popover"
                class="far fa-question-circle ml-2"
              />
              <b-popover
                target="reminder-days-popover"
                triggers="hover"
                placement="top"
              >
                {{ formLabels.reminder_days_help_text }}
              </b-popover>
            </template>
            <b-input-group append="Days">
              <b-form-input
                name="compliance-document-reminder_days"
                v-model="$v.formData.reminder_days.$model"
                :state="$v.formData.reminder_days.$dirty ? !$v.formData.reminder_days.$error : null"
                type="text"
              />
            </b-input-group>
            <b-form-invalid-feedback
              v-if="$v.formData.reminder_days.$dirty"
              :state="$v.formData.reminder_days.$dirty ? !$v.formData.reminder_days.$error : null"
              class="compliance-document-reminder_days-feedback"
            >
              <span v-if="!$v.formData.reminder_days.required">Please enter a number.</span>
              <span v-if="!$v.formData.reminder_days.integer">Please enter a number.</span>
              <span v-if="!$v.formData.reminder_days.serverFailed">
                {{ serverErrors.reminder_days }}
              </span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          lg="6"
        >
          <b-form-group
            :label="formLabels.reminder_frequency"
            label-for="compliance-document-reminder_frequency"
          >
            <frequency-select
              name="compliance-document-reminder_frequency"
              v-model="$v.formData.reminder_frequency.$model"
              :select-class="{ 'is-invalid': $v.formData.reminder_frequency.$dirty && $v.formData.reminder_frequency.$error }"
              :options="reminderFrequencyOptions"
              placeholder="Only on the day of expiry"
            />
            <b-form-invalid-feedback
              v-if="$v.formData.reminder_frequency.$dirty"
              class="compliance-document-reminder_frequency-feedback"
            >
              <span v-if="!$v.formData.reminder_frequency.serverFailed">{{ serverErrors.reminder_frequency }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-form-group label-for="compliance-document-requires_coverage_verification">
            <b-form-checkbox
              switch
              class="compliance-document-requires_coverage_verification"
              v-model="$v.formData.requires_coverage_verification.$model"
            >
              {{ formLabels.requires_coverage_verification }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>
    </template>

    <hr class="mt-4">

    <b-row>
      <b-col
        offset-lg="6"
        lg="3"
        order="2"
        order-lg="1"
      >
        <b-button
          variant="link-dark"
          block
          class="compliance-document-close-button"
          @click="$emit('on-cancel')"
        >
          Cancel
        </b-button>
      </b-col>
      <b-col
        lg="3"
        order="1"
        order-lg="2"
      >
        <b-button
          class="compliance-document-submit-button"
          block
          type="submit"
          variant="primary"
          :disabled="processing || !showFormFields"
        >
          Save
        </b-button>
      </b-col>
    </b-row>
  </b-form>
</template>

<script>
import { required, maxLength, integer, email, requiredIf } from 'vuelidate/lib/validators';
import formMixin from '@/mixins/formMixin';
import UserSelect from '@/components/admin/users/Select.vue';
import FrequencySelect from '@/components/shared/FrequencySelect.vue';
import CategoryButtonSelect from '@/components/admin/complianceDocuments/CategoryButtonSelect.vue';
import CategorySelect from '@/components/admin/complianceDocuments/CategorySelect.vue';

import complianceDocumentTerminology from '@/utils/complianceDocumentTerminology';
import { COMPLIANCE_DOCUMENT_TYPE_INSURANCE_CERTIFICATE } from '@/constants/complianceDocuments';
import { USER_TECHNICIAN_CATEGORY } from '@/constants/users';

export default {
  name: 'ComplianceDocumentsForm',
  components: {
    UserSelect,
    FrequencySelect,
    CategoryButtonSelect,
    CategorySelect,
  },
  mixins: [formMixin],
  props: {
    number: String,
    document_type: String,
    category: String,
    issuer_name: String,
    email: String,
    phone: String,
    state: String,
    reminder_frequency: String,
    reminder_days: Number,
    user_id: Number,
    user: Object,
    requires_coverage_verification: Boolean,
    start_date: String,
    end_date: String,
    newForm: {
      type: Boolean,
      default: true,
    },
    reminderFrequencyOptions: {
      type: Array,
      default: () => [],
    },
    categoryOptions: {
      type: Array,
      default: () => [],
    },
    documentTypeOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      formData: {
        number: this.number,
        document_type: this.document_type,
        category: this.category,
        issuer_name: this.issuer_name,
        email: this.email,
        phone: this.phone,
        state: this.state,
        reminder_frequency: this.reminder_frequency,
        reminder_days: this.reminder_days,
        user_id: this.user_id,
        user: this.user,
        requires_coverage_verification: this.requires_coverage_verification,
        start_date: this.start_date,
        end_date: this.end_date,
      },
      userSelectParams: { category: USER_TECHNICIAN_CATEGORY, active: true },
    };
  },
  validations: {
    formData: {
      number: {
        required,
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('number');
        },
      },
      document_type: {
        required: requiredIf(function () {
          return this.newForm;
        }),
        serverFailed() {
          return !this.hasServerErrors('document_type');
        },
      },
      category: {
        required,
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('category');
        },
      },
      issuer_name: {
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('issuer_name');
        },
      },
      email: {
        email,
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('email');
        },
      },
      phone: {
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('phone');
        },
      },
      state: {
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('state');
        },
      },
      reminder_frequency: {
        serverFailed() {
          return !this.hasServerErrors('reminder_frequency');
        },
      },
      reminder_days: {
        integer,
        required,
        serverFailed() {
          return !this.hasServerErrors('reminder_days');
        },
      },
      user_id: {
        required: requiredIf(function () {
          return this.newForm;
        }),
        serverFailed() {
          return !this.hasServerErrors('user_id');
        },
      },
      requires_coverage_verification: {},
      start_date: {
        serverFailed() {
          return !this.hasServerErrors('start_date');
        },
      },
      end_date: {
        required: requiredIf(function () {
          return this.newForm;
        }),
        serverFailed() {
          return !this.hasServerErrors('end_date');
        },
      },
    },
  },
  computed: {
    terminology() {
      return complianceDocumentTerminology(this.formData.document_type);
    },
    formLabels() {
      return {
        number: 'Number *',
        document_type: 'Document Type',
        category: `${this.terminology.category} *`,
        issuer_name: `${this.terminology.issuerName}${this.newForm ? ' *' : ''}`,
        email: this.terminology.email,
        phone: this.terminology.phone,
        state: 'State',
        reminder_frequency: 'Reminder Frequency',
        reminder_days: 'Reminder Days *',
        reminder_days_help_text: this.terminology.reminderDaysHelpText,
        user_id: 'User *',
        requires_coverage_verification: this.terminology.requiresVerificationLabel,
        start_date: this.terminology.startDateLabel,
        end_date: this.terminology.endDateLabel,
        end_date_required: this.terminology.endDateValidationText,
      };
    },
    showFormFields() {
      return !this.newForm || !!this.formData.document_type;
    },
    showInsuranceCategoryInput() {
      return this.formData.document_type === COMPLIANCE_DOCUMENT_TYPE_INSURANCE_CERTIFICATE;
    },
    form() {
      const form = {
        number: this.formData.number,
        category: this.formData.category,
        issuer_name: this.formData.issuer_name,
        email: this.formData.email,
        phone: this.formData.phone,
        state: this.formData.state,
        reminder_frequency: this.formData.reminder_frequency,
        reminder_days: this.formData.reminder_days,
        user_id: this.formData.user_id,
        requires_coverage_verification: this.formData.requires_coverage_verification,
        current_coverage_attributes: {
          start_date: this.formData.start_date,
          end_date: this.formData.end_date,
        },
      };

      if (this.newForm) {
        form.document_type = this.formData.document_type;
      }

      return form;
    },
  },
  methods: {
    onUserChange(user) {
      this.formData.user_id = user ? user.id : null;
      this.formData.user = user;

      if (this.newForm) {
        this.formData.state = user ? user.state : '';
      }
    },
  },
};
</script>
